type StaticMapOptions = {
  lat: number;
  lon: number;
  width: number;
  height: number;
  zoom: number;
  scale?: number;
  format?: 'png' | 'jpg';
};

export type StaticMapBounds = {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
};

// Google Static Maps accepts zoom 0-21; /api/static-map validates the same range.
const MIN_STATIC_MAP_ZOOM = 0;
const MAX_STATIC_MAP_ZOOM = 21;

// Cloud-styled "Static map" in the hotpads Google project (edit in Map
// Management); the env override covers environments whose signing key lacks it.
const HOTPADS_STATIC_MAP_ID = process.env.NEXT_PUBLIC_GOOGLE_MAPS_STATIC_MAP_ID ?? '83226b537847112eb3c8eb77';

export function buildStaticMapUrl({ lat, lon, width, height, zoom, scale = 2, format }: StaticMapOptions): string {
  let url = 'https://maps.googleapis.com/maps/api/staticmap?';
  url += `size=${width}x${height}&`;
  url += `zoom=${zoom}&`;
  url += `scale=${scale}&`;
  if (format) {
    url += `format=${format}&`;
  }
  url += `center=${lat},${lon}&`;

  url += `map_id=${HOTPADS_STATIC_MAP_ID}&`;

  // Remove trailing &
  if (url.endsWith('&')) {
    url = url.slice(0, -1);
  }

  return url;
}

const GLOBE_WIDTH = 256;

export function getZoomForBoundingBox(
  minLat: number,
  maxLat: number,
  minLon: number,
  maxLon: number,
  mapWidth: number,
): number {
  if (minLat === maxLat || minLon === maxLon) {
    return 14;
  }

  let angle = maxLon - minLon;
  if (angle < 0) {
    angle += 360;
  }

  let zoom = Math.floor(Math.log((mapWidth * 360) / angle / GLOBE_WIDTH) / Math.LN2);

  // Compare with the wrapped span so antimeridian-crossing bounds don't
  // register as negative-width (always-tall) boxes.
  if (angle / (maxLat - minLat) < 1) {
    zoom--;
  }

  // Near-degenerate bounds push the raw math far past Google's 0-21 range.
  return Math.min(MAX_STATIC_MAP_ZOOM, Math.max(MIN_STATIC_MAP_ZOOM, zoom));
}

/**
 * Unsigned Google Static Maps URL centered on the given bounds. Pair with
 * buildStaticMapProxyUrl so the /api/static-map route signs and fetches it.
 */
export function buildStaticMapUrlForBounds(
  bounds: StaticMapBounds,
  { width, height, scale, format }: { width: number; height: number; scale?: number; format?: StaticMapOptions['format'] },
): string {
  const lat = (bounds.minLat + bounds.maxLat) / 2;
  // minLon > maxLon means the bounds cross the antimeridian: average on the
  // wrapped axis and normalize back to [-180, 180).
  const lon =
    bounds.minLon > bounds.maxLon
      ? ((bounds.minLon + bounds.maxLon + 360) / 2 + 540) % 360 - 180
      : (bounds.minLon + bounds.maxLon) / 2;
  const zoom = getZoomForBoundingBox(bounds.minLat, bounds.maxLat, bounds.minLon, bounds.maxLon, width);

  return buildStaticMapUrl({ lat, lon, width, height, zoom, scale, format });
}

export function buildStaticMapProxyUrl(unsignedUrl: string): string {
  return `/api/static-map?url=${encodeURIComponent(unsignedUrl)}`;
}
